// ============================================================================
// PLAYER POTENTIALS — Drives progression between seasons
// Uses real ages, preserves base ratings, prevents compounding
// Floor: 65, Ceiling: 99
// ============================================================================
import { getPlayerRating, getBasePlayerRating } from './playerRatings';

const FLOOR = 65;
const CEILING = 99;

// Hand-set potentials for notable players
const NOTABLE_POTENTIALS = {
  // Young superstars (under 26, very high ceiling)
  'Anthony Edwards': 97, 'Luka Doncic': 97, 'Victor Wembanyama': 98,
  'Shai Gilgeous-Alexander': 96, 'Jayson Tatum': 95, 'Tyrese Haliburton': 93,
  'Anthony Davis': 93, 'Ja Morant': 94, 'Paolo Banchero': 94,
  'Chet Holmgren': 94, 'Evan Mobley': 94, 'Scottie Barnes': 93,
  'Jalen Brunson': 92, 'Tyrese Maxey': 93, 'Alperen Sengun': 92,
  'Desmond Bane': 89, 'Franz Wagner': 92, 'Cade Cunningham': 93,
  'Jalen Green': 91, 'LaMelo Ball': 93, 'Darius Garland': 91,
  'Donovan Mitchell': 92, 'Trae Young': 92, 'Bam Adebayo': 91,
  'Zion Williamson': 95, 'Brandon Ingram': 89, "De'Aaron Fox": 91,
  'Devin Booker': 93, 'Karl-Anthony Towns': 91, 'Jaylen Brown': 91,
  'Lauri Markkanen': 90, 'Jaren Jackson Jr.': 91, 'Jalen Williams': 93,
  'Amen Thompson': 91, 'Ausar Thompson': 89, 'Scoot Henderson': 90,
  'Jaime Jaquez Jr.': 87, 'Dereck Lively II': 90, 'Brandon Miller': 92,
  'Gradey Dick': 88, 'Cam Thomas': 90, 'Keyonte George': 89,
  'Reed Sheppard': 90, 'Zach Edey': 87, 'Stephon Castle': 90,
  'Dalton Knecht': 86, 'Alex Sarr': 92, 'Jared McCain': 89,
  'Dyson Daniels': 88, 'Keegan Murray': 88, 'Walker Kessler': 86,
  'Jabari Smith Jr.': 90, 'Anfernee Simons': 89, 'Tyler Herro': 89,
  'Coby White': 87, 'Jalen Suggs': 87, 'Josh Giddey': 88,
  'Jonathan Kuminga': 90, 'Tari Eason': 87, 'Mark Williams': 87,
  'Cooper Flagg': 95, 'Dylan Harper': 92, 'Ace Bailey': 93,
  'Tre Johnson': 91, 'Khaman Maluach': 90, 'VJ Edgecombe': 90,
  'Kasparas Jakucionis': 89,

  // Prime stars (at or near ceiling — potential = slight above current)
  'Nikola Jokic': 97, 'Giannis Antetokounmpo': 96, 'Joel Embiid': 95,
  'Kevin Durant': 93, 'Stephen Curry': 92, 'Kawhi Leonard': 92,
  'Damian Lillard': 89, 'Jimmy Butler': 88, 'Paul George': 87,
  'Pascal Siakam': 88, 'Domantas Sabonis': 90, 'Julius Randle': 87,
  'OG Anunoby': 87, 'Mikal Bridges': 86, 'Dejounte Murray': 87,
  'Fred VanVleet': 85, 'Jrue Holiday': 87, 'Derrick White': 86,
  'Jarrett Allen': 87, 'Myles Turner': 85,

  // Aging vets (potential BELOW current — decline is certain)
  'LeBron James': 88, 'Chris Paul': 72, 'Russell Westbrook': 72,
  'Kyle Lowry': 70, 'Al Horford': 72, 'Brook Lopez': 76,
  'DeMar DeRozan': 78, 'Rudy Gobert': 82, 'Kristaps Porzingis': 85,
  'Nikola Vucevic': 78, 'Mike Conley': 70, 'James Harden': 82,

  // 2026 draft class potentials
  'Darryn Peterson': 95, 'AJ Dybantsa': 94, 'Cameron Boozer': 93,
  'Caleb Wilson': 91, 'Kingston Flemings': 90, 'Mikel Brown Jr.': 88,
  'Keaton Wagler': 87, 'Darius Acuff Jr.': 89, 'Nate Ament': 87,
  'Labaron Philon': 86, 'Jayden Quaintance': 88, 'Yaxel Lendeborg': 83,
  'Brayden Burries': 84, 'Dailyn Swain': 84, 'Koa Peat': 86,
};

/**
 * Get a player's potential rating.
 * Notable players use hand-set values. Others use age-based formula.
 * IMPORTANT: Age must be the REAL age, not a hash.
 */
export function getPlayerPotential(name, age) {
  if (NOTABLE_POTENTIALS[name]) return NOTABLE_POTENTIALS[name];

  const ovr = getPlayerRating(name) || 70;

  // Age-based potential formula
  if (!age || age < 22) return Math.min(CEILING, ovr + 6 + Math.floor(Math.random() * 4));  // +6 to +9
  if (age < 25) return Math.min(CEILING, ovr + 3 + Math.floor(Math.random() * 3));          // +3 to +5
  if (age < 28) return Math.min(CEILING, ovr + Math.floor(Math.random() * 2));               // +0 to +1
  if (age < 31) return Math.max(FLOOR, ovr - 1);                                             // -1 (declining)
  if (age < 34) return Math.max(FLOOR, ovr - 3);                                             // -3
  return Math.max(FLOOR, ovr - 6);                                                           // -6 (steep decline)
}

/**
 * Apply one year of progression/regression.
 * 
 * KEY DESIGN:
 * - baseOvr is the ORIGINAL rating from playerRatings.js (NEVER the override)
 * - currentOvr is what the player is rated NOW (base + accumulated changes)
 * - Age 31+: changes are NEGATIVE ONLY
 * - Max swing: +3 (young) or -3 (old) per year
 * - Floor: 65, Ceiling: 99
 * - Notable players can't drop more than 8 below their ORIGINAL base
 */
export function progressPlayer(name, currentOvr, age, seasonYear, draftedThisYear = false) {
  // Rookies don't progress until the year AFTER they are drafted
  if (draftedThisYear) return currentOvr;
  
  const potential = getPlayerPotential(name, age);
  // CRITICAL: Use the ORIGINAL static rating, not the overridden one
  const baseOvr = getBasePlayerRating(name) || currentOvr;
  const gap = potential - currentOvr;

  let change;
  if (age < 23) {
    // Young: grow toward potential (+0 to +3)
    const growth = Math.min(3, Math.max(0, Math.round(gap * 0.3 + (Math.random() - 0.3) * 1.5)));
    change = growth;
  } else if (age < 26) {
    // Developing: moderate growth (+0 to +2)
    const growth = Math.min(2, Math.max(-1, Math.round(gap * 0.2 + (Math.random() - 0.4) * 1)));
    change = growth;
  } else if (age < 29) {
    // Prime: mostly stable, slight growth if below potential (-1 to +1)
    if (gap > 3) {
      // Still has room to grow — slight positive bias
      change = Math.random() < 0.6 ? 1 : 0;
    } else {
      change = Math.round((Math.random() - 0.5) * 1.2);
      change = Math.max(-1, Math.min(1, change));
    }
  } else if (age < 32) {
    // Aging: NEGATIVE ONLY (-1 to -2)
    change = -(1 + Math.floor(Math.random() * 2));
  } else {
    // Veteran: steeper decline (-1 to -3)
    change = -(1 + Math.floor(Math.random() * 3));
  }

  // Hard ceiling: never exceed potential
  let newOvr = currentOvr + change;
  if (newOvr > potential) newOvr = potential;

  // Hard floor for notable players: don't drop more than 8 below their ORIGINAL base
  if (NOTABLE_POTENTIALS[name]) {
    const minAllowed = Math.max(FLOOR, baseOvr - 8);
    if (newOvr < minAllowed) newOvr = minAllowed;
  }

  // For ALL players: don't drop more than 12 below original base
  const absoluteMin = Math.max(FLOOR, baseOvr - 12);
  if (newOvr < absoluteMin) newOvr = absoluteMin;

  return Math.max(FLOOR, Math.min(CEILING, newOvr));
}

export { NOTABLE_POTENTIALS };
export default { getPlayerPotential, progressPlayer, NOTABLE_POTENTIALS };
